/**
 * AudioDevicePicker — the mic/speaker selects for the softphone.
 *
 * Chrome softphones use the OS "default communications device", which is
 * invisible to the recruiter: calls can silently ride the laptop's built-in mic
 * from three feet away while a perfectly good headset sits on their head. This
 * picker makes the routing visible and overridable. Selections persist per
 * machine (crm.phone.micDeviceId / crm.phone.speakerDeviceId) and apply live
 * mid-call; SoftphoneContext owns all of that — this component is only the UI.
 *
 * Mount it ONLY from a user click (the call-dock popover, the dialer
 * disclosure): on mount it may probe the mic once to unlock device labels on a
 * machine where the site has never held mic permission.
 */
import { useEffect, useRef } from 'react';
import { Volume2 } from 'lucide-react';
import { useSoftphone } from '../contexts/SoftphoneContext';

const selectCls =
  'w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 ' +
  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ' +
  'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const labelCls = 'mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500';

/** 0.35s 440Hz test beep as an in-memory WAV — no asset to fetch, and an
 * HTMLAudioElement (unlike WebAudio) can be pointed at the chosen sink. */
function makeBeepUrl(): string {
  const rate = 24000;
  const n = Math.floor(rate * 0.35);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i += 1) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i += 1) {
    // Short fade-in/out so the beep doesn't click.
    const fade = Math.min(1, i / (rate * 0.01), (n - i) / (rate * 0.05));
    v.setInt16(44 + i * 2, Math.round(Math.sin((i / rate) * 2 * Math.PI * 440) * 0.3 * fade * 32767), true);
  }
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}

export default function AudioDevicePicker() {
  const {
    inputDevices, outputDevices, inputDeviceId, outputDeviceId,
    defaultInputLabel, defaultOutputLabel,
    outputSelectionSupported, setAudioInputDevice, setAudioOutputDevice, refreshAudioDevices,
  } = useSoftphone();
  const beepUrlRef = useRef<string | null>(null);

  useEffect(() => {
    refreshAudioDevices({ probeForLabels: true });
    return () => {
      if (beepUrlRef.current) { URL.revokeObjectURL(beepUrlRef.current); beepUrlRef.current = null; }
    };
  }, [refreshAudioDevices]);

  // A saved device that isn't currently connected still shows (marked), so the
  // preference is visible and clearable instead of silently reading as default.
  const inputMissing = Boolean(inputDeviceId) && !inputDevices.some((d) => d.deviceId === inputDeviceId);
  const outputMissing = Boolean(outputDeviceId) && !outputDevices.some((d) => d.deviceId === outputDeviceId);

  const testSpeaker = async () => {
    try {
      if (!beepUrlRef.current) beepUrlRef.current = makeBeepUrl();
      const el = new Audio(beepUrlRef.current);
      const anyEl = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (outputDeviceId && !outputMissing && anyEl.setSinkId) {
        try { await anyEl.setSinkId(outputDeviceId); } catch { /* default sink */ }
      }
      await el.play();
    } catch { /* inside a click gesture — autoplay can't block; nothing to surface */ }
  };

  return (
    <div className="space-y-2">
      <label className="block">
        <span className={labelCls}>Microphone</span>
        <select
          value={inputDeviceId}
          onChange={(e) => setAudioInputDevice(e.target.value)}
          className={selectCls}
          aria-label="Microphone"
        >
          {/* Naming the device the default resolves to is the point: "far away"
              callers are usually a call quietly riding the laptop mic. */}
          <option value="">{defaultInputLabel ? `System default — ${defaultInputLabel}` : 'System default'}</option>
          {inputDevices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
          ))}
          {inputMissing && <option value={inputDeviceId}>Saved mic (not connected)</option>}
        </select>
      </label>

      {outputSelectionSupported ? (
        <label className="block">
          <span className={labelCls}>Speaker</span>
          <div className="flex items-center gap-1.5">
            <select
              value={outputDeviceId}
              onChange={(e) => setAudioOutputDevice(e.target.value)}
              className={selectCls}
              aria-label="Speaker"
            >
              <option value="">{defaultOutputLabel ? `System default — ${defaultOutputLabel}` : 'System default'}</option>
              {outputDevices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
              ))}
              {outputMissing && <option value={outputDeviceId}>Saved speaker (not connected)</option>}
            </select>
            <button
              type="button"
              onClick={testSpeaker}
              title="Play a test tone through the selected speaker"
              className="inline-flex h-[30px] shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-2 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Volume2 className="h-3.5 w-3.5" /> Test
            </button>
          </div>
        </label>
      ) : (
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          This browser can’t choose a speaker — call audio uses the system default output.
        </p>
      )}

      <p className="text-[11px] leading-snug text-gray-400 dark:text-gray-500">
        Applies to this call and future calls. Saved on this computer.
      </p>
    </div>
  );
}
